import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Divider, Dropdown, Select, Spin, Table } from "antd";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import svg12 from "../../../assets/svg/svg12.svg";
import svg15 from "../../../assets/svg/svg15.svg";
import svg90 from "../../../assets/svg/svg90.svg";
import svg27 from "../../../assets/svg/svg27.svg";
import svg29 from "../../../assets/svg/svg29.svg";
import BreadCrumb from "../../../layout/BreadCrumb";
import ProductionDataCard from "./ProductionDataCard";
import ProductionDataView from "./ProductionDataView";
import {
  fetchCrops,
  fetchData,
  generateReport,
} from "../../../features/fetch/fetchSlice";
import { setActiveObj } from "../../../features/obj/objSlice";
import { numberWithCommas } from "../../../utils";
import { financialYearData } from "../../../data";


function ProductionDataTable() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const url = "api/v1/scheme-production-data/list";
  const urlSummary = "api/v1/scheme-production-data/summary";
  const urlScheme = `${process.env.REACT_APP_API_BASE_SCHEME}/sch/get_schemes`;

  const {
    loading,
    data,
    dataScheme,
    dataSummary,
    count,
    loadingGenerateReport,
  } = useSelector((state) => ({
    loading: state.fetch.loading,
    data: state.fetch.data[url] || [],
    dataSummary: state.fetch.data[urlSummary] || [],
    loadingGenerateReport: state.fetch.loadingGenerateReport,
    count: state.fetch.count[url] || 0,
    dataScheme: state.fetch.data[urlScheme] || [],
  }));

  const { crops } = useSelector((state) => state.fetch);

  const [prodd, setProdd] = useState("");
  const [schemeId, setSchemeId] = useState(null);
  const [cropId, setCropId] = useState(null);
  const [financialYear, setFinancialYear] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [openDrawer, setOpenDrawer] = useState(false);
  const showDrawer = () => {
    setOpenDrawer(true);
  };
  const onClose = () => {
    setOpenDrawer(false);
  };

  const [activeItemId, setActiveItemId] = useState(null);

  const handleDownload = async (item) => {
    await setActiveItemId(item?.id);
    const res = await dispatch(
      generateReport({
        url: "api/v1/scheme-production-data/download",
        id: item?.id,
      })
    );
    if (res?.payload) {
      const blobData = res?.payload?.data;
      const headers = res?.payload?.headers;
      const contentType =
        headers["content-type"] || "application/octet-stream";
      const contentDisposition = headers["content-disposition"];
      let fileName = "production-data.pdf";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) fileName = match[1];
      }
      const blob = new Blob([blobData], { type: contentType });
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(fileUrl);
      toast.success(`${fileName} downloaded successfully`);
    } else {
      toast.error("Failed to download production data");
    }
    await setActiveItemId(null);
  };

  const handleView = async (item) => {
    await setProdd(item);
    await dispatch(
      fetchData({
        url: "api/v1/production-varieties/list",
        productionDataId: item?.id,
        limit: 1000,
        start: 0,
      })
    );
    await showDrawer();
  };

  const handleAdd = async () => {
    await dispatch(setActiveObj({ key: "schemeProductionData", data: null }));
    await navigate("/production-data-add");
  };

  const handleEdit = async () => {
    await dispatch(setActiveObj({ key: "schemeProductionData", data: prodd }));
    await navigate("/production-data-add");
  };

  const settingItems = [
    {
      key: "1",
      label: (
        <div onClick={() => handleView(prodd)}>
          <Link className="flex actions-text gap-x-[.75rem] my-2 w-4 h-4 ">
            <img src={svg15} alt="svg15" /> View
          </Link>
          <div className="mt-[1rem]">
            <Divider />
          </div>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div onClick={() => handleDownload(prodd)}>
          <Link className="flex actions-text gap-x-[.75rem] my-2 w-4 h-4 ">
            {loadingGenerateReport && activeItemId === prodd?.id ? (
              <Spin size="small" />
            ) : (
              <img src={svg90} alt="svg90" />
            )}
            Download
          </Link>
          <div className="mt-[1rem]">
            <Divider />
          </div>
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div onClick={handleEdit}>
          <Link className="flex actions-text gap-x-[.75rem] my-2 w-4 h-4 ">
            <img src={svg12} alt="svg12" /> Edit
          </Link>
          <div className="mt-[1rem]">
            <Divider />
          </div>
        </div>
      ),
    },
  ];

  const columns = [
    {
      title: "Scheme",
      dataIndex: ["scheme", "name"],
      sorter: (a, b) => a?.scheme?.name?.localeCompare(b?.scheme?.name),
      render: (item, record) => (
        <button
          type="button"
          onClick={() => handleView(record)}
          className="cursor-pointer"
        >
          {item ?? "--"}
        </button>
      ),
    },
    {
      title: "County",
      dataIndex: "countyName",
    },
    {
      title: "FY",
      dataIndex: "financialYear",
      sorter: (a, b) => a?.financialYear?.localeCompare(b?.financialYear),
    },
    {
      title: "Crop",
      dataIndex: ["crop", "name"],
      render: (item) =>
        item ? (
          <div className="inline-block rounded-[8px] bg-[#F1F3F5] px-[8px] py-[4px] !text-[12px] text-[#111928]">
            {item}
          </div>
        ) : (
          "--"
        ),
    },
    {
      title: "Acreage",
      dataIndex: "cropAcreage",
      align: "right",
      render: (item) => (item || item === 0 ? numberWithCommas(item) : "--"),
      sorter: (a, b) => a?.cropAcreage - b?.cropAcreage,
    },
    {
      title: "Production (MT)",
      dataIndex: "totalProduction",
      align: "right",
      render: (item) => (item || item === 0 ? numberWithCommas(item) : "--"),
      sorter: (a, b) => a?.totalProduction - b?.totalProduction,
    },
    {
      title: "MT/Acre",
      dataIndex: "productionPerAcre",
      align: "right",
      render: (item) =>
        item || item === 0 ? Number(item).toFixed(2) : "--",
      sorter: (a, b) => a?.productionPerAcre - b?.productionPerAcre,
    },
    {
      title: "Seasons",
      dataIndex: "seasonCount",
      align: "right",
    },
    {
      title: "Infrastructure",
      dataIndex: ["operationalStatus", "name"],
      render: (item) => item ?? "--",
    },
 
    {
      title: "Actions",
      render: (item) => (
        <>
          <Dropdown
            overlayStyle={{
              width: "250px",
            }}
            trigger={"click"}
            menu={{ items: settingItems }}
            placement="bottom"
          >
            <button onClick={() => setProdd(item)}>
              <img src={svg27} alt="svg27" />
            </button>
          </Dropdown>
        </>
      ),
    },
  ];

  async function fetchProductionData(page, size, sch, crop, fy) {
    // The list and the summary share one filter definition, so send both the
    // same object and refetch both whenever a filter changes.
    // start is a row offset here, not a page number.
    const filters = {
      schemeId: sch === undefined ? schemeId : sch,
      cropId: crop === undefined ? cropId : crop,
      financialYear: fy === undefined ? financialYear : fy,
      start: (page ?? pageIndex) * (size ?? pageSize),
      limit: size ?? pageSize,
    };
    await dispatch(fetchData({ url: url, ...filters }));
    await dispatch(fetchData({ url: urlSummary, ...filters }));
  }

  const handleSchemeFilter = async (value) => {
    await setSchemeId(value ?? null);
    await setPageIndex(0);
    await fetchProductionData(0, undefined, value ?? null, undefined, undefined);
  };

  const handleCropFilter = async (value) => {
    await setCropId(value ?? null);
    await setPageIndex(0);
    await fetchProductionData(0, undefined, undefined, value ?? null, undefined);
  };

  const handleFyFilter = async (value) => {
    await setFinancialYear(value ?? null);
    await setPageIndex(0);
    await fetchProductionData(0, undefined, undefined, undefined, value ?? null);
  };

  useEffect(() => {
    fetchProductionData();
    dispatch(fetchCrops());
    dispatch(
      fetchData({
        url: urlScheme,
        limit: null,
        start: 0,
      })
    );
  }, []);

  return (
    <div className="lg:min-h-[88vh] flex flex-col">
      <BreadCrumb breadcrumbs={[{ title: "Scheme Production Data" }]} />
      <div className="white_card mt-[1.25rem] flex-1">
        <div className="flex lg:flex-row flex-col lg:items-center justify-between gap-y-4">
          <div>
            <div className="form-add-h !text-darkBlue">
              Scheme Production Data
            </div>
            <div className="proj_desc mt-[.25rem]">
              Production, inputs, markets and infrastructure indicators per
              scheme and financial year.
            </div>
          </div>
          <div className="w-[250px]">
            <button onClick={() => handleAdd()} className="cstm-btn-2">
              <img src={svg29} alt="svg29" />
              <span className="">Add new production data</span>
            </button>
          </div>
        </div>

        <ProductionDataCard data={dataSummary} />

        <div className="flex lg:flex-row flex-col gap-4 mt-[1.75rem]">
          <div className="lg:w-[15rem] w-full">
            <Select
              className="w-full"
              allowClear
              placeholder="All schemes"
              value={schemeId}
              onChange={handleSchemeFilter}
              options={
                dataScheme?.length > 0 &&
                dataScheme?.map((item) => ({
                  value: item?.schId,
                  label: item?.schName,
                }))
              }
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label?.toLocaleLowerCase() ?? "").includes(
                  input?.toLocaleLowerCase()
                )
              }
            />
          </div>
          <div className="lg:w-[12rem] w-full">
            <Select
              className="w-full"
              allowClear
              placeholder="All crops"
              value={cropId}
              onChange={handleCropFilter}
              options={
                crops?.length > 0 &&
                crops?.map((item) => ({
                  value: item?.cropId,
                  label: item?.cropName,
                }))
              }
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label?.toLocaleLowerCase() ?? "").includes(
                  input?.toLocaleLowerCase()
                )
              }
            />
          </div>
          <div className="lg:w-[14rem] w-full">
            <Select
              className="w-full"
              allowClear
              placeholder="All financial years"
              value={financialYear}
              onChange={handleFyFilter}
              options={financialYearData}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label?.toLocaleLowerCase() ?? "").includes(
                  input?.toLocaleLowerCase()
                )
              }
            />
          </div>
        </div>

        <div className="max-w-full w-full overflow-x-auto">
          <Table
            rowSelection={false}
            className="mt-[1.75rem] w-full"
            scroll={{
              x: "max-content",
            }}
            rowKey={(record) => record?.id}
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{
              position: ["bottomRight"],
              current: pageIndex + 1,
              total: count,
              pageSize: pageSize,
              onChange: (page, size) => {
                setPageIndex(page - 1);
                setPageSize(size);
                fetchProductionData(page - 1, size);
              },
              showSizeChanger: false,
              hideOnSinglePage: true,
            }}
          />
        </div>
      </div>

      <ProductionDataView onClose={onClose} open={openDrawer} prodd={prodd} />
    </div>
  );
}

export default ProductionDataTable;
